import { c as _c } from "react/compiler-runtime";
import { FormInputSearch, FormSelect, Tooltip, toast } from '@systemeio/ui';
import { getServerSideFlagsProps } from 'growthbook/server';
import { FlagsPropsType } from 'growthbook/types';
import CourseCreateModal from 'modules/courses/components/create-modal';
import TabMenu from 'modules/courses/components/tab-menu';
import { coursesDependencies } from 'modules/courses/constants/courses-dependencies';
import { CoursesLoco } from 'modules/courses/constants/courses-loco';
import { activateCourse, duplicateCourse, removeCourse } from 'modules/courses/courses/api/courses-api';
import { useCourses } from 'modules/courses/courses/hooks/use-courses';
import { CourseFilterInterface } from 'modules/courses/courses/types/course-filter-interface';
import { CourseDuplicationStatusEnum, CourseEditorTypeEnum, CourseInterface } from 'modules/courses/courses/types/course-interface';
import { getCourseCurriculumUrl } from 'modules/courses/courses/utils/get-course-curriculum-url';
import { getCourseSettingsUrl } from 'modules/courses/courses/utils/get-course-settings-url';
import { StatusDataInterface } from 'modules/funnels/types/funnel-filter-interface';
import { ShareResourceEnum } from 'modules/share/enum/share-resource-enum';
import { getShareUrl } from 'modules/share/utils/get-share-url';
import { NextPage } from 'next';
import React from 'react';
import ConfirmationModal from 'shared/components/confirmation-modal';
import LinkShareModal from 'shared/components/link-share-modal';
import LinkWithoutPrefetch from 'shared/components/link-without-prefetch';
import ListingLayout from 'shared/components/listing-layout';
import { Table } from 'shared/components/table/table';
import { ColumnDefinitionInterface } from 'shared/components/table/types/column-interface';
import { RequestModuleEnum } from 'shared/enums/request-module-enum';
import { BooleanStatusEnum } from 'shared/enums/status-enum';
import { useLocoTranslation } from 'shared/hooks/use-loco-translation';
import { useRevalidateModules } from 'shared/hooks/use-revalidate-modules';
import { useFilterPagination } from 'shared/route-query/hooks/use-filter-pagination';
import { getBaseServerSideProps, getCombinedServerSideProps } from 'shared/utils/get-base-server-side-props';
import { CheckCircleIcon, TimeCircleIcon } from '@systemeio/ui/icons';
const defaultFilters: CourseFilterInterface = {
  search: '',
  status: undefined
};
const Course: NextPage<FlagsPropsType> = () => {
  const $ = _c(125);
  const {
    t
  } = useLocoTranslation();
  const {
    setPagination,
    pagination,
    setLimit,
    limit,
    filter,
    setFilter
  } = useFilterPagination(defaultFilters);
  let t0;
  if ($[0] !== filter || $[1] !== limit || $[2] !== pagination) {
    t0 = {
      limit,
      pagination,
      filter
    };
    $[0] = filter;
    $[1] = limit;
    $[2] = pagination;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  const {
    courses,
    mutate,
    counter
  } = useCourses(t0);
  const {
    revalidateModules
  } = useRevalidateModules();
  let t1;
  if ($[4] !== t) {
    t1 = () => Object.keys(BooleanStatusEnum).map(key => {
      const status = key as keyof typeof BooleanStatusEnum;
      return {
        id: status,
        caption: t(BooleanStatusEnum[status])
      };
    });
    $[4] = t;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const getStatusData = t1;
  let t2;
  if ($[6] !== t) {
    t2 = t("global.title");
    $[6] = t;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== t2) {
    t3 = {
      key: "name",
      header: t2,
      widthPercent: 65,
      onRender: _temp
    };
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== t) {
    t4 = t("global.type");
    $[10] = t;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== t) {
    t5 = course_0 => <span className="truncate">{course_0.courseType === CourseEditorTypeEnum.ClassicCourse ? t("dashboard.store.advanced_course.title") : t("dashboard.light_course.title")}</span>;
    $[12] = t;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== t4 || $[15] !== t5) {
    t6 = {
      key: "type",
      header: t4,
      widthPercent: 20,
      onRender: t5
    };
    $[14] = t4;
    $[15] = t5;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== t) {
    t7 = t("global.status");
    $[17] = t;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[19] !== t) {
    t8 = course_1 => {
      const label = course_1.duplicationStatus === CourseDuplicationStatusEnum.Pending ? t("dashboard.course.duplication_status_pending_description") : course_1.active ? t("global.active") : t("global.inactive");
      const icon = course_1.duplicationStatus === CourseDuplicationStatusEnum.Pending ? <TimeCircleIcon className="fill-yellow h-[16px] w-[16px] sm:h-[20px] sm:w-[20px]" data-test-id="course-status-icon-pending" /> : <CheckCircleIcon className={`h-[16px] w-[16px] sm:h-[20px] sm:w-[20px] ${course_1.active && "fill-green"}`} data-test-id={`course-status-icon-${course_1.active ? "active" : "inactive"}`} />;
      return <div className="flex items-center lg:ml-4"><Tooltip label={<span data-test-id="course-status-tooltip">{label}</span>} mode="hover" key="status-tooltip">{icon}</Tooltip></div>;
    };
    $[19] = t;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] !== t7 || $[22] !== t8) {
    t9 = {
      key: "active",
      header: t7,
      widthPercent: 15,
      onRender: t8
    };
    $[21] = t7;
    $[22] = t8;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  let t10;
  if ($[24] !== t3 || $[25] !== t6 || $[26] !== t9) {
    t10 = [t3, t6, t9];
    $[24] = t3;
    $[25] = t6;
    $[26] = t9;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  const columns = t10;
  let t11;
  if ($[28] !== revalidateModules || $[29] !== t) {
    t11 = async course_2 => {
      const {
        duplicationStatus
      } = (await duplicateCourse(course_2.id)).data;
      if (duplicationStatus === CourseDuplicationStatusEnum.Pending) {
        toast.success(t("dashboard.course.course_duplicated"));
      } else {
        toast.success(t("dashboard.course.course_duplicating"));
      }
      await revalidateModules(coursesDependencies, true);
    };
    $[28] = revalidateModules;
    $[29] = t;
    $[30] = t11;
  } else {
    t11 = $[30];
  }
  const onDuplicateCourse = t11;
  let t12;
  if ($[31] !== mutate || $[32] !== revalidateModules) {
    t12 = async course_3 => {
      const update = course_3.active ? "deactivate" : "activate";
      await activateCourse(update, course_3.id);
      await mutate(data => {
        if (data) {
          return {
            items: data.items.map(item => {
              if (item.id === course_3.id) {
                item = {
                  ...item,
                  active: !item.active
                };
              }
              return item;
            }),
            hasMore: data.hasMore
          };
        }
      }, false);
      await revalidateModules(coursesDependencies);
    };
    $[31] = mutate;
    $[32] = revalidateModules;
    $[33] = t12;
  } else {
    t12 = $[33];
  }
  const onActivateCourse = t12;
  let t13;
  if ($[34] !== revalidateModules) {
    t13 = async course_4 => {
      await removeCourse(course_4.id);
      await revalidateModules([...coursesDependencies, RequestModuleEnum.MembershipLightCourseCurriculum, RequestModuleEnum.MembershipCourseCurriculum, RequestModuleEnum.MembershipCourseLecture], true);
    };
    $[34] = revalidateModules;
    $[35] = t13;
  } else {
    t13 = $[35];
  }
  const onRemoveCourse = t13;
  let t14;
  if ($[36] !== revalidateModules) {
    t14 = async () => await revalidateModules([RequestModuleEnum.MembershipCourse], true);
    $[36] = revalidateModules;
    $[37] = t14;
  } else {
    t14 = $[37];
  }
  const handleSave = t14;
  let t15;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <TabMenu />;
    $[38] = t15;
  } else {
    t15 = $[38];
  }
  let t16;
  if ($[39] !== t) {
    t16 = t("dashboard.courses.title_plural");
    $[39] = t;
    $[40] = t16;
  } else {
    t16 = $[40];
  }
  let t17;
  if ($[41] !== filter || $[42] !== setFilter) {
    t17 = {
      filter,
      setFilter,
      defaultFilters
    };
    $[41] = filter;
    $[42] = setFilter;
    $[43] = t17;
  } else {
    t17 = $[43];
  }
  let t18;
  if ($[44] !== pagination || $[45] !== setPagination) {
    t18 = {
      pagination,
      setPagination
    };
    $[44] = pagination;
    $[45] = setPagination;
    $[46] = t18;
  } else {
    t18 = $[46];
  }
  let t19;
  if ($[47] !== limit || $[48] !== setLimit) {
    t19 = {
      limit,
      setLimit
    };
    $[47] = limit;
    $[48] = setLimit;
    $[49] = t19;
  } else {
    t19 = $[49];
  }
  let t20;
  if ($[50] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = {
      field: "search",
      onRender: _temp2
    };
    $[50] = t20;
  } else {
    t20 = $[50];
  }
  let t21;
  if ($[51] !== getStatusData) {
    t21 = [t20, {
      field: "status",
      onRender: (value_0, onChange_0) => <FormSelect key="courses-status-filter" data={getStatusData()} onChange={data_0 => onChange_0(data_0)} value={value_0} />
    }];
    $[51] = getStatusData;
    $[52] = t21;
  } else {
    t21 = $[52];
  }
  let t22;
  if ($[53] !== t17 || $[54] !== t18 || $[55] !== t19 || $[56] !== t21) {
    t22 = {
      filterer: t17,
      paginator: t18,
      limiter: t19,
      testAttributePostfix: "filter-btn",
      filterable: t21
    };
    $[53] = t17;
    $[54] = t18;
    $[55] = t19;
    $[56] = t21;
    $[57] = t22;
  } else {
    t22 = $[57];
  }
  let t23;
  if ($[58] !== handleSave) {
    t23 = <CourseCreateModal onSave={handleSave} />;
    $[58] = handleSave;
    $[59] = t23;
  } else {
    t23 = $[59];
  }
  let t24;
  if ($[60] !== t) {
    t24 = t("dashboard.courses.title");
    $[60] = t;
    $[61] = t24;
  } else {
    t24 = $[61];
  }
  let t25;
  if ($[62] !== limit || $[63] !== setLimit) {
    t25 = {
      limit,
      setLimit
    };
    $[62] = limit;
    $[63] = setLimit;
    $[64] = t25;
  } else {
    t25 = $[64];
  }
  let t26;
  if ($[65] !== pagination || $[66] !== setPagination) {
    t26 = {
      pagination,
      setPagination
    };
    $[65] = pagination;
    $[66] = setPagination;
    $[67] = t26;
  } else {
    t26 = $[67];
  }
  let t27;
  if ($[68] !== t) {
    t27 = t("dashboard.actions.title.open");
    $[68] = t;
    $[69] = t27;
  } else {
    t27 = $[69];
  }
  let t28;
  if ($[70] !== t27) {
    t28 = {
      name: "view-course",
      caption: t27,
      setHref: _temp3,
      openInNew: true,
      shouldRender: _temp4
    };
    $[70] = t27;
    $[71] = t28;
  } else {
    t28 = $[71];
  }
  let t29;
  if ($[72] !== t) {
    t29 = t("dashboard.course.curriculum");
    $[72] = t;
    $[73] = t29;
  } else {
    t29 = $[73];
  }
  let t30;
  if ($[74] !== t29) {
    t30 = {
      name: "curriculum",
      setHref: _temp5,
      caption: t29,
      openInNew: false,
      isNext: true,
      shouldRender: _temp6
    };
    $[74] = t29;
    $[75] = t30;
  } else {
    t30 = $[75];
  }
  let t31;
  if ($[76] !== t) {
    t31 = t("dashboard.actions.title.settings");
    $[76] = t;
    $[77] = t31;
  } else {
    t31 = $[77];
  }
  let t32;
  if ($[78] !== t31) {
    t32 = {
      name: "settings",
      setHref: _temp7,
      caption: t31,
      openInNew: false,
      isNext: true,
      shouldRender: _temp8
    };
    $[78] = t31;
    $[79] = t32;
  } else {
    t32 = $[79];
  }
  let t33;
  if ($[80] !== t28 || $[81] !== t30 || $[82] !== t32) {
    t33 = [t28, t30, t32];
    $[80] = t28;
    $[81] = t30;
    $[82] = t32;
    $[83] = t33;
  } else {
    t33 = $[83];
  }
  let t34;
  if ($[84] !== t) {
    t34 = t("dashboard.actions.title.share");
    $[84] = t;
    $[85] = t34;
  } else {
    t34 = $[85];
  }
  let t35;
  if ($[86] !== t) {
    t35 = (opened, onClose, course_11, key_0) => <LinkShareModal opened={opened} link={getShareUrl(ShareResourceEnum.Course, course_11.hash)} title={t("dashboard.course.share_course")} label={t("dashboard.course.share_course_text")} onClose={onClose} key={key_0} />;
    $[86] = t;
    $[87] = t35;
  } else {
    t35 = $[87];
  }
  let t36;
  if ($[88] !== t34 || $[89] !== t35) {
    t36 = {
      name: "share-course",
      caption: t34,
      onActionRender: t35,
      shouldRender: _temp9
    };
    $[88] = t34;
    $[89] = t35;
    $[90] = t36;
  } else {
    t36 = $[90];
  }
  let t37;
  if ($[91] !== t) {
    t37 = t("dashboard.actions.title.duplicate");
    $[91] = t;
    $[92] = t37;
  } else {
    t37 = $[92];
  }
  let t38;
  if ($[93] !== onDuplicateCourse || $[94] !== t) {
    t38 = (opened_0, onClose_0, course_13, key_1) => <ConfirmationModal confirmationText={t("dashboard.course.confirm_duplicate")} key={key_1} opened={opened_0} onClose={onClose_0} onConfirm={() => onDuplicateCourse(course_13)} />;
    $[93] = onDuplicateCourse;
    $[94] = t;
    $[95] = t38;
  } else {
    t38 = $[95];
  }
  let t39;
  if ($[96] !== t37 || $[97] !== t38) {
    t39 = {
      name: "duplicate-course",
      caption: t37,
      onActionRender: t38,
      shouldRender: _temp10
    };
    $[96] = t37;
    $[97] = t38;
    $[98] = t39;
  } else {
    t39 = $[98];
  }
  let t40;
  if ($[99] !== t36 || $[100] !== t39) {
    t40 = [t36, t39];
    $[99] = t36;
    $[100] = t39;
    $[101] = t40;
  } else {
    t40 = $[101];
  }
  let t41;
  if ($[102] !== onActivateCourse) {
    t41 = {
      onChange: course_15 => onActivateCourse(course_15),
      confirmationText: "dashboard.course.activate_confirmation_course",
      shouldRender: _temp11
    };
    $[102] = onActivateCourse;
    $[103] = t41;
  } else {
    t41 = $[103];
  }
  let t42;
  if ($[104] !== onRemoveCourse) {
    t42 = {
      onChange: course_17 => onRemoveCourse(course_17),
      shouldRender: _temp12
    };
    $[104] = onRemoveCourse;
    $[105] = t42;
  } else {
    t42 = $[105];
  }
  let t43;
  if ($[106] !== t33 || $[107] !== t40 || $[108] !== t41 || $[109] !== t42) {
    t43 = {
      openLink: t33,
      custom: t40,
      activate: t41,
      remove: t42
    };
    $[106] = t33;
    $[107] = t40;
    $[108] = t41;
    $[109] = t42;
    $[110] = t43;
  } else {
    t43 = $[110];
  }
  let t44;
  if ($[111] !== columns || $[112] !== courses || $[113] !== t24 || $[114] !== t25 || $[115] !== t26 || $[116] !== t43) {
    t44 = <Table caption={t24} data={courses} columns={columns} limiter={t25} paginator={t26} testAttributePostfix="courses-table" actions={t43} />;
    $[111] = columns;
    $[112] = courses;
    $[113] = t24;
    $[114] = t25;
    $[115] = t26;
    $[116] = t43;
    $[117] = t44;
  } else {
    t44 = $[117];
  }
  let t45;
  if ($[118] !== counter || $[119] !== courses || $[120] !== t16 || $[121] !== t22 || $[122] !== t23 || $[123] !== t44) {
    t45 = <>{t15}<ListingLayout data={courses} counter={counter} title={t16} filterData={t22} customHeaderElement={t23} type="compact">{t44}</ListingLayout></>;
    $[118] = counter;
    $[119] = courses;
    $[120] = t16;
    $[121] = t22;
    $[122] = t23;
    $[123] = t44;
    $[124] = t45;
  } else {
    t45 = $[124];
  }
  return t45;
};
export const getServerSideProps = getCombinedServerSideProps(getBaseServerSideProps(CoursesLoco, 'header.top_menu.courses'), getServerSideFlagsProps());
export default Course;
function _temp(course) {
  const link = course.duplicationStatus === CourseDuplicationStatusEnum.Pending ? course.name : <LinkWithoutPrefetch className="text-brand-primary hover:text-text-secondary ring-brand-primary rounded-xs w-full outline-hidden ring-offset-1 focus-visible:ring-2" href={getCourseCurriculumUrl({
    courseId: course.id,
    courseType: course.courseType
  })}><span data-test-id="course-name-link">{course.name}</span></LinkWithoutPrefetch>;
  return <div className="truncate">{link}</div>;
}
function _temp2(value, onChange) {
  return <FormInputSearch key="courses-search-filter" onClear={() => onChange("")} value={value} onChange={e => onChange(e.target.value)} testAttributePostfix="search-input" />;
}
function _temp3(course_5) {
  return course_5.loginLinkUrl;
}
function _temp4(course_6) {
  return course_6.duplicationStatus !== CourseDuplicationStatusEnum.Pending;
}
function _temp5(course_7) {
  return getCourseCurriculumUrl({
    courseId: course_7.id,
    courseType: course_7.courseType
  });
}
function _temp6(course_8) {
  return course_8.duplicationStatus !== CourseDuplicationStatusEnum.Pending;
}
function _temp7(course_9) {
  return getCourseSettingsUrl({
    courseId: course_9.id,
    courseType: course_9.courseType
  });
}
function _temp8(course_10) {
  return course_10.duplicationStatus !== CourseDuplicationStatusEnum.Pending;
}
function _temp9(course_12) {
  return course_12.duplicationStatus !== CourseDuplicationStatusEnum.Pending && course_12.courseType === CourseEditorTypeEnum.ClassicCourse;
}
function _temp10(course_14) {
  return course_14.duplicationStatus !== CourseDuplicationStatusEnum.Pending && course_14.courseType === CourseEditorTypeEnum.ClassicCourse;
}
function _temp11(course_16) {
  return course_16.duplicationStatus !== CourseDuplicationStatusEnum.Pending;
}
function _temp12(course_18) {
  return course_18.duplicationStatus !== CourseDuplicationStatusEnum.Pending;
}